import {useQueryClient} from '@tanstack/react-query';
import {createFileRoute} from '@tanstack/react-router';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'tolgee';

import {useCancelSyntheticDefectPipelineMutation} from '~api/automl.mutations';
import {AutoMlQueryKeys, useLatestSyntheticDefectPipelineQuery} from '~api/automl.queries';
import {pushRoute, replaceRoute, Route as RouteEnum} from '~utils/routeUtil';
import {selectCurrentProjectId} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

import {showNotification} from '~components/common/Notification';
import {BaseLayout} from '~components/_layout/BaseLayout';
import {LayoutContent} from '~components/_layout/layoutComponents';
import {GenerateDataLoading} from '~components/models/finetune/generate-data/GenerateDataLoading';
import {ModelFooter} from '~components/models/ModelFooter';
import {ModelWorkflowStep} from '~components/models/ModelWorkflowSteps';
import {useCurrentModel} from 'src/contexts/ModelContext';
import {ModelProvider} from 'src/contexts/ModelProvider';

export const Route = createFileRoute('/model/finetune/generate-data-loading')({
  component: GenerateDataLoadingPage,
});

function GenerateDataLoadingPage() {
  return (
    <ModelProvider mode='model-pages'>
      <BaseLayout showBackButton customContentAndFooter>
        <GenerateDataLoadingContent />
      </BaseLayout>
    </ModelProvider>
  );
}

function GenerateDataLoadingContent() {
  const {t} = useTranslation('common');
  const queryClient = useQueryClient();
  const projectId = useAppSelector(selectCurrentProjectId);
  const {currentModel} = useCurrentModel();
  const [hasFreshData, setHasFreshData] = useState(false);
  const hasSeenRunning = useRef(false);

  // Invalidate stale cache on mount so we get a fresh fetch.
  useEffect(() => {
    if (!currentModel?.id) return;
    queryClient
      .invalidateQueries({queryKey: AutoMlQueryKeys.latestImageGenerationPipeline(projectId, currentModel.id)})
      .then(() => setHasFreshData(true));
  }, [queryClient, projectId, currentModel?.id]);

  const generationPipelineQuery = useLatestSyntheticDefectPipelineQuery({modelId: currentModel?.id});
  const generationPipeline = generationPipelineQuery.data;
  const generationJobStatus = generationPipeline?.statusData?.job?.status;
  const generationPipelineState = generationPipeline?.statusData?.pipeline?.status;
  const isGenerationCanceled = generationJobStatus === 'canceled' || !!generationPipeline?.statusData?.canceled;
  const isGenerationJobFailed = generationJobStatus === 'failed';
  // Fall back to pipeline state when job is not yet recorded (early-trigger race window).
  const isGenerationRunning =
    !isGenerationCanceled &&
    (generationJobStatus === 'pending' ||
      generationJobStatus === 'running' ||
      (generationJobStatus === undefined &&
        (generationPipelineState === 'pending' || generationPipelineState === 'running')));
  const isGenerationComplete = generationJobStatus === 'success' || generationPipelineState === 'completed';
  const generationJobLink = generationPipeline?.jobLink;

  useLatestSyntheticDefectPipelineQuery({
    modelId: currentModel?.id,
    refetchInterval: isGenerationRunning ? 5000 : undefined,
  });

  const cancelGenerationMutation = useCancelSyntheticDefectPipelineMutation();

  useEffect(() => {
    if (isGenerationRunning) {
      hasSeenRunning.current = true;
    }
  }, [isGenerationRunning]);

  useEffect(() => {
    if (!currentModel?.id) return;
    if (!hasFreshData) return;
    if (generationPipelineQuery.isFetching) return;
    if (isGenerationRunning) return;

    if (isGenerationComplete) {
      replaceRoute(RouteEnum.modelFinetuneGenerateDataSuccess);
    } else if (isGenerationJobFailed) {
      showNotification({message: t('pipelineFailed'), severity: 'error', id: 'generation-pipeline-failed'});
      replaceRoute(RouteEnum.modelFinetune);
    } else if (isGenerationCanceled || !hasSeenRunning.current) {
      replaceRoute(RouteEnum.modelFinetune);
    }
  }, [
    currentModel?.id,
    hasFreshData,
    generationPipelineQuery.isFetching,
    isGenerationRunning,
    isGenerationComplete,
    isGenerationCanceled,
    isGenerationJobFailed,
    t,
  ]);

  const handleCancel = () => {
    if (generationPipeline?.pipelineId && currentModel?.id) {
      cancelGenerationMutation.mutate({pipelineId: generationPipeline.pipelineId, modelId: currentModel.id});
    }
    queryClient.setQueryData(AutoMlQueryKeys.latestImageGenerationPipeline(projectId, currentModel?.id), null);
    pushRoute(RouteEnum.modelFinetune);
  };

  return (
    <>
      <LayoutContent sx={{justifyContent: 'center', alignItems: 'center'}}>
        <GenerateDataLoading onCancel={handleCancel} jobLink={generationJobLink || undefined} />
      </LayoutContent>
      <ModelFooter activeStep={ModelWorkflowStep.finetune} />
    </>
  );
}
